import { ListClinic } from '@chiroup/core/types/Clinic.type';
import { User } from '@chiroup/core/types/User.type';
import { useEffect, useState } from 'react';
import { Route, Routes, useLocation, useParams } from 'react-router-dom';
import { useAdminPermissions } from '../../../hooks/useAdminPermissions';
import useClinic from '../../../hooks/useClinic';
import ClinicDocumentHistory from '../../documents/ClinicDocumentHistory';
import EventLogClinicDetail from '../../event-logs/EventLogClinicDetail';
import Header from '../../layout/Header';
import { SecureCaseList } from '../../secure-cases/SecureCases';
import Integrations from '../Integrations';
import ClinicAdHocReports from './ClinicAdHocReports';
import ClinicExports from './ClinicExports';
import ClinicGeneral from './ClinicGeneral';
import ClinicSupportAccess from './ClinicSupportAccess';
import ClinicTabs from './ClinicTabs';
import ClinicTestPatient from './ClinicTestPatient';
import ClinicTestPatientReport from './ClinicTestPatientReport';
import ClinicUsers from './ClinicUsers';
import Billing from './billing/Billing';
import ClinicDebug from './debug/ClinicDebug';
import ClinicLocations from './locations/ClinicLocations';
import Patients from './patients/Patients';
import PaymentProcessorConfig from './payment-processor/PaymentProcessorConfig';

const ClinicContainer = ({ user }: { user: any }) => {
  const [clinic, setClinic] = useState<Partial<ListClinic>>();
  const [clinicUsers, setClinicUsers] = useState<User[]>([]);
  const { clinicID } = useParams();
  const { data, update, refetch, isUpdating, del } = useClinic(clinicID);
  const { pathname } = useLocation();
  const { hasPermission } = useAdminPermissions();

  useEffect(() => {
    if (data) {
      const { users: nextClinicUsers, ...theClinic } = data;
      setClinic(theClinic);
      setClinicUsers(nextClinicUsers || []);
    }
  }, [data]);

  const hasExportAccess = hasPermission('clinic.exports');
  const hasAdHocAccess = hasPermission('clinic.adhoc_reports');
  const hasDebugAccess = hasPermission('clinic.debug');
  const hasDisabledPatientsAccess = hasPermission('clinic.view_disabled_patients');
  const hasBillingAccess = hasPermission('clinic.view_billing');
  const hasIntegrationsAccess = hasPermission('clinic.view_integrations');
  const hasPaymentsAccess = hasPermission('clinic.view_pp_config');
  const hasClinicUsersAccess = hasPermission('clinic.view_users');
  const hasLocationsAccess = hasPermission('clinic.view_location');
  const hasEventLogsAccess = hasPermission('clinic.view_event_logs');
  const hasSupportAccess = hasPermission('clinic.support_access');
  const hasSecureCasesAccess = hasPermission('secure_cases.view');
  const hasTestPatientAccess = hasPermission('clinic.test_patient');

  return clinic ? (
    <>
      {/* Printing a test-patient condition report should produce only the
          report itself, so the clinic chrome stays off the printed page. */}
      <div className="print:hidden">
        <Header title={clinic.name} />
        <ClinicTabs
          active={pathname}
          id={clinicID}
          hasExportAccess={hasExportAccess}
          hasDevtoolsAccess={hasDebugAccess}
          hasAdHocAccess={hasAdHocAccess}
          hasSupportAccess={hasSupportAccess || hasSecureCasesAccess}
          hasTestPatientAccess={hasTestPatientAccess}
          hasDocumentsAccess={hasPermission('documents.view')}
        />
      </div>
      <Routes>
        <Route
          path="/documents"
          element={
            hasPermission('documents.view') ? (
              <ClinicDocumentHistory key={clinicID} clinicId={Number(clinicID)} />
            ) : null
          }
        />
        <Route
          path="/test-patient/reports/:reportId"
          element={
            hasTestPatientAccess ? <ClinicTestPatientReport clinicId={Number(clinicID)} /> : null
          }
        />
        <Route
          path="/test-patient"
          element={
            hasTestPatientAccess ? (
              <ClinicTestPatient
                key={clinicID}
                clinicId={Number(clinicID)}
                clinicName={clinic.name || 'ChiroUp'}
                users={clinicUsers}
              />
            ) : null
          }
        />
        <Route
          path="/"
          element={
            <ClinicGeneral
              clinic={clinic}
              update={update}
              isUpdating={isUpdating}
              del={del}
              user={user}
            />
          }
        />
        <Route
          path={`/users`}
          element={
            hasClinicUsersAccess ? (
              <ClinicUsers
                users={clinicUsers}
                clinicID={clinic.ID}
                refetch={refetch}
                clinic={clinic}
              />
            ) : null
          }
        />
        <Route
          path={`/locations`}
          element={
            hasLocationsAccess ? (
              <ClinicLocations
                locations={clinic.locations}
                clinicId={clinic.ID}
                isEHR={clinic.ehr}
                setClinic={setClinic}
              />
            ) : null
          }
        />
        <Route path={`/billing`} element={hasBillingAccess ? <Billing clinic={clinic} /> : null} />
        <Route
          path={`/event-logs`}
          element={
            hasEventLogsAccess ? <EventLogClinicDetail clinicId={clinic.ID as number} /> : null
          }
        />
        <Route
          path={`/integrations`}
          element={hasIntegrationsAccess ? <Integrations clinic={clinic} /> : null}
        />
        <Route
          path={`/payments`}
          element={
            hasPaymentsAccess ? (
              <PaymentProcessorConfig
                clinic={{ ID: clinic.ID as number, name: clinic.name || '' }}
              />
            ) : null
          }
        />
        <Route
          path={`/patients`}
          element={hasDisabledPatientsAccess ? <Patients clinic={clinic} /> : null}
        />
        <Route path={`/debug`} element={hasDebugAccess ? <ClinicDebug clinic={clinic} /> : null} />
        <Route path={`/exports`} element={hasExportAccess ? <ClinicExports /> : null} />
        <Route
          path={`/ad-hoc-reports`}
          element={hasAdHocAccess ? <ClinicAdHocReports clinic={clinic} /> : null}
        />
        <Route
          path={`/support-access`}
          element={
            <>
              {hasSupportAccess && <ClinicSupportAccess clinicId={clinic.ID as number} />}
              {hasSecureCasesAccess && (
                <SecureCaseList key={clinicID} clinicId={Number(clinicID)} />
              )}
            </>
          }
        />
      </Routes>
    </>
  ) : null;
};

export default ClinicContainer;
